import api, { type ApiServiceError } from './api';
import { 
  UserResponse, 
  CreateUserRequest,
  UpdateUserProfileRequest 
} from '../types/profile';
import { ApiResponse } from '../types/api';

/**
 * Interface pour la réponse réelle du backend /users/me
 * ✨ CORRIGÉ: Le backend retourne directement les données utilisateur (pas dans un objet "user")
 * Structure: { success: true, data: { id, email, givenName, familyName, financialInstitutionId, ... } }
 */
interface BackendUserData {
  id: string;
  email: string;
  emailVerified?: boolean;
  name?: string;
  givenName?: string | null;  // Le backend utilise givenName (pas firstName)
  familyName?: string | null; // Le backend utilise familyName (pas lastName)
  picture?: string | null;
  phone?: string | null;
  phoneVerified?: boolean;
  address?: string | null;
  idNumber?: string | null;
  idType?: string | null;
  idStatus?: 'verified' | 'pending' | 'rejected' | null;
  role?: string;
  bio?: string | null;
  userType?: 'sme' | 'financial_institution';
  companyId?: string | null;
  // Lien vers l'entité Customer (customer-service). Nécessaire pour persister
  // la juridiction (Ville/Province) via PUT /customers/:customerId.
  customerId?: string | null;
  financialInstitutionId?: string | null;  // ✨ Directement dans l'objet (pas institutionId)
  isCompanyOwner?: boolean;
  // État du dossier côté back-office (miroir de CustomerStatus) et motif de
  // suspension. Sans ces deux champs, l'interface ne pouvait pas savoir si un
  // compte était validé ou encore en attente.
  accountStatus?: string | null;
  accountSuspensionReason?: string | null;
  createdAt?: string;
  updatedAt?: string;
}

/**
 * ✨ CORRIGÉ: Mappe la réponse backend vers le format UserResponse
 * Le backend retourne directement l'objet utilisateur, pas { user: {...} }
 */
function mapBackendUserToUserResponse(backendData: BackendUserData): UserResponse {
  // ✨ Le backend retourne directement les données, pas dans un objet "user"
  
  // Construire le nom complet à partir de name, givenName ou familyName
  const fullName = backendData.name || 
    [backendData.givenName, backendData.familyName].filter(Boolean).join(' ').trim() || 
    backendData.email;
  
  console.log('🔄 Mapping utilisateur backend:', {
    id: backendData.id,
    name: backendData.name,
    givenName: backendData.givenName,
    familyName: backendData.familyName,
    financialInstitutionId: backendData.financialInstitutionId,
    companyId: backendData.companyId,
    userType: backendData.userType
  });
  
  return {
    id: backendData.id,
    email: backendData.email,
    name: fullName,
    // ✨ Le backend utilise directement "financialInstitutionId"
    financialInstitutionId: backendData.financialInstitutionId || undefined,
    companyId: backendData.companyId || undefined,
    // Lien Customer (customer-service) — utilisé pour la juridiction Ville/Province
    customerId: backendData.customerId || undefined,
    // ✨ userType provient du backend, ou est déduit des associations
    // Si aucune association, userType reste undefined (utilisateur n'a pas encore soumis de formulaire)
    userType: backendData.userType || 
              (backendData.financialInstitutionId ? 'financial_institution' : 
               backendData.companyId ? 'sme' : undefined),
    picture: backendData.picture || undefined,
    phone: backendData.phone || undefined,
    address: backendData.address || undefined,
    role: backendData.role,
    idNumber: backendData.idNumber || undefined,
    idStatus: backendData.idStatus || undefined,
    emailVerified: backendData.emailVerified === true,
    phoneVerified: backendData.phoneVerified,
    accountStatus: (backendData.accountStatus as UserResponse['accountStatus']) || undefined,
    accountSuspensionReason: backendData.accountSuspensionReason || undefined,
    createdAt: backendData.createdAt || new Date().toISOString(),
    updatedAt: backendData.updatedAt || new Date().toISOString(),
  };
}

/**
 * Service pour la gestion des utilisateurs, conforme à la documentation API
 */
class UserApiService {
  private baseUrl = '/users';

  async getCurrentUser(forceRefresh = false): Promise<UserResponse> {
    try {
      // ✨ CORRIGÉ: Le backend retourne { success: true, data: {...utilisateur...} }
      const response = await api.get<ApiResponse<BackendUserData>>(`${this.baseUrl}/me`, undefined, forceRefresh);
      
      console.log('🔍 Réponse brute du backend /me:', response);
      
      // ✨ response.data contient directement les données utilisateur
      const mappedUser = mapBackendUserToUserResponse(response.data);
      
      console.log('✅ Utilisateur mappé:', {
        id: mappedUser.id,
        email: mappedUser.email,
        financialInstitutionId: mappedUser.financialInstitutionId,
        companyId: mappedUser.companyId,
        userType: mappedUser.userType
      });
      
      return mappedUser;
    } catch (error) {
      console.warn('Erreur lors de la récupération du profil utilisateur:', error);
      throw error;
    }
  }

  /**
   * Crée un utilisateur lors du premier signup
   * Cette méthode est appelée automatiquement après un signup Auth0 réussi
   * ✨ CORRIGÉ: Utilise /users/sync pour envoyer les données Auth0 complètes
   */
  async createFirstTimeUser(userData: CreateUserRequest): Promise<UserResponse> {
    try {
      // Récupérer l'auth0Id depuis le localStorage
      const auth0UserData = localStorage.getItem('auth0_user');
      let auth0Id = '';
      let givenName = '';
      let familyName = '';
      
      if (auth0UserData) {
        try {
          const auth0User = JSON.parse(auth0UserData);
          auth0Id = auth0User.sub || auth0User.id || '';
          givenName = auth0User.given_name || auth0User.nickname || '';
          familyName = auth0User.family_name || '';
          console.log('🔍 Auth0 User Data récupéré:', {
            auth0Id,
            email: auth0User.email,
            name: auth0User.name,
            givenName,
            familyName
          });
        } catch (e) {
          console.warn('⚠️ Impossible de parser les données Auth0');
        }
      }
      
      // ✨ IMPORTANT: Utiliser l'endpoint /sync qui accepte l'auth0Id et les vraies données
      console.log('🔧 Synchronisation du premier utilisateur via /users/sync:', {
        auth0Id,
        email: userData.email,
        name: userData.name
      });
      
      const syncData = {
        auth0Id: auth0Id,
        email: userData.email,
        name: userData.name,
        firstName: givenName || userData.name?.split(' ')[0] || '',
        lastName: familyName || userData.name?.split(' ').slice(1).join(' ') || '',
        // ✨ userType est optionnel - le backend le détermine selon le formulaire soumis
        // Si fourni, sert d'indication initiale
        ...(userData.userType && { userType: userData.userType }),
        isFirstTimeUser: true,
      };
      
      console.log('📤 Données envoyées au sync:', syncData);
      
      const response = await api.post<ApiResponse<UserResponse>>(`${this.baseUrl}/sync`, syncData);
      console.log('✅ Utilisateur synchronisé avec succès côté backend:', response.data);
      return response.data;
    } catch (error) {
      // api.ts rejette des objets ApiServiceError (pas des Error) ; on tolère aussi la forme axios { response.status }
      const httpError = error as (Partial<ApiServiceError> & { response?: { status?: number } }) | null | undefined;
      // Si l'endpoint /sync n'existe pas, fallback sur l'ancien endpoint
      if (httpError?.status === 404 || httpError?.response?.status === 404) {
        console.warn('⚠️ Endpoint /sync non disponible, tentative avec /users');
        
        const auth0UserData = localStorage.getItem('auth0_user');
        let auth0Id = '';
        if (auth0UserData) {
          try {
            const auth0User = JSON.parse(auth0UserData);
            auth0Id = auth0User.sub || auth0User.id || '';
          } catch {
            // Données Auth0 locales illisibles : on poursuit avec un auth0Id vide
          }
        }
        
        const response = await api.post<ApiResponse<UserResponse>>(`${this.baseUrl}`, {
          ...userData,
          auth0Id: auth0Id, // ✨ Ajouter l'auth0Id même sur l'ancien endpoint
          isFirstTimeUser: true,
        });
        return response.data;
      }
      
      console.error('❌ Erreur lors de la création du premier utilisateur:', error);
      throw error;
    }
  }

  async updateUserProfile(data: UpdateUserProfileRequest): Promise<UserResponse> {
    const response = await api.patch<ApiResponse<UserResponse>>(`${this.baseUrl}/me`, data);
    return response.data;
  }

  async acceptTerms(version = '1.0'): Promise<{ success: boolean; data: { termsAccepted: boolean; termsAcceptedAt: string } }> {
    const response = await api.patch<{ success: boolean; data: { termsAccepted: boolean; termsAcceptedAt: string } }>(
      `${this.baseUrl}/me/accept-terms`,
      { version }
    );
    return response;
  }

  /**
   * ✨ Resynchronise les données utilisateur avec Auth0
   * Utile pour corriger les données temporaires générées par erreur
   */
  async resyncWithAuth0Data(): Promise<UserResponse> {
    try {
      // Récupérer les données Auth0 stockées localement
      const auth0UserData = localStorage.getItem('auth0_user');
      
      if (!auth0UserData) {
        throw new Error('Aucune donnée Auth0 disponible localement');
      }
      
      const auth0User = JSON.parse(auth0UserData);
      const auth0Id = auth0User.sub || auth0User.id || '';
      
      // Vérifier si l'email est un email temporaire
      const currentUser = await this.getCurrentUser();
      const isTemporaryEmail = currentUser.email?.includes('@wanzo.temp') || 
                              currentUser.email?.includes('temp-');
      const isTemporaryName = currentUser.name?.startsWith('User-');
      
      if (!isTemporaryEmail && !isTemporaryName) {
        console.log('✅ Données utilisateur déjà correctes, pas de resync nécessaire');
        return currentUser;
      }
      
      console.log('🔄 Resynchronisation des données utilisateur avec Auth0...');
      console.log('📧 Email Auth0:', auth0User.email);
      console.log('👤 Nom Auth0:', auth0User.name);
      
      // Utiliser l'endpoint /sync pour mettre à jour
      const syncData = {
        auth0Id: auth0Id,
        email: auth0User.email,
        name: auth0User.name || auth0User.nickname || auth0User.email,
        firstName: auth0User.given_name || auth0User.nickname || '',
        lastName: auth0User.family_name || '',
        picture: auth0User.picture,
      };
      
      const response = await api.post<ApiResponse<UserResponse>>(`${this.baseUrl}/sync`, syncData);
      console.log('✅ Données utilisateur resynchronisées avec succès');
      return response.data;
    } catch (error) {
      // Fallback: mettre à jour via PATCH /me
      console.warn('⚠️ Endpoint /sync non disponible, tentative via PATCH /me');
      
      const auth0UserData = localStorage.getItem('auth0_user');
      if (auth0UserData) {
        const auth0User = JSON.parse(auth0UserData);
        return this.updateUserProfile({
          email: auth0User.email,
          name: auth0User.name || auth0User.nickname || auth0User.email,
        });
      }
      
      throw error;
    }
  }

  // Ancienne méthode pour synchroniser avec Auth0 (via userinfo endpoint)
  async syncWithAuth0(): Promise<UserResponse> {
    try {
      // Récupérer les vraies données Auth0
      const auth0Response = await fetch('https://dev-tezmln0tk0g1gouf.eu.auth0.com/userinfo', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth0_token')}`
        }
      });

      if (auth0Response.ok) {
        const auth0User = await auth0Response.json();
        
        console.log('🔍 Données Auth0 userinfo:', auth0User);
        
        // Mettre à jour avec le vrai email et nom
        return this.updateUserProfile({
          email: auth0User.email,
          name: auth0User.name || auth0User.nickname
        });
      }
      
      throw new Error('Impossible de récupérer les données Auth0');
    } catch (error) {
      console.error('❌ Erreur sync Auth0:', error);
      throw error;
    }
  }

  async changeUserType(userType: 'sme' | 'financial_institution'): Promise<{
    id: string;
    userType: string;
    message: string;
  }> {
    const response = await api.patch<ApiResponse<{
      id: string;
      userType: string;
      message: string;
    }>>(`${this.baseUrl}/me/type`, { userType });
    return response.data;
  }

  async verifyPhone(phone: string, code: string): Promise<{
    phoneVerified: boolean;
    message: string;
  }> {
    const response = await api.post<ApiResponse<{
      phoneVerified: boolean;
      message: string;
    }>>(`${this.baseUrl}/me/verify-phone`, { phone, code });
    return response.data;
  }

  /**
   * @deprecated Utiliser cloudinaryService.uploadDocument() puis updateProfile({ identityDocument: url }) à la place.
   * Cette méthode envoie le fichier directement au backend, ce qui n'est pas recommandé.
   * Le flux correct: Fichier → Cloudinary → URL → Backend
   */
  async uploadIdDocument(file: File, documentType: 'passport' | 'national_id' | 'driving_license'): Promise<{
    url: string;
    documentType: string;
    message: string;
  }> {
    console.warn('⚠️ uploadIdDocument est déprécié. Utilisez cloudinaryService.uploadDocument() puis updateProfile()');
    const formData = new FormData();
    formData.append('document', file);
    formData.append('documentType', documentType);

    const response = await api.postFormData<ApiResponse<{
      url: string;
      documentType: string;
      message: string;
    }>>(`${this.baseUrl}/me/identity-document`, formData);
    return response.data;
  }

  /**
   * @deprecated Utiliser cloudinaryService.uploadAvatar() puis updateProfile({ avatar: url }) à la place.
   * Cette méthode envoie le fichier directement au backend, ce qui n'est pas recommandé.
   * Le flux correct: Fichier → Cloudinary → URL → Backend
   */
  async uploadProfilePhoto(file: File): Promise<{ url: string }> {
    console.warn('⚠️ uploadProfilePhoto est déprécié. Utilisez cloudinaryService.uploadAvatar() puis updateProfile()');
    const response = await api.upload<ApiResponse<{ url: string }>>(
      `${this.baseUrl}/me/avatar`,
      file,
      'photo'
    );
    return response.data;
  }
}

export const userApiService = new UserApiService();
export default userApiService;
